import { uniq } from 'lodash-es';
import { StartLocationType } from '@lp/features/RoutePlanner/types';
import { LegLocationTypeEnum, RouteLocationType } from '@/api/apis/route';
import { RouteTypeSettingsEnum, StartLocationObjectType } from '@/api/apis/mission-planner';
import { getLocationAddress } from '@/utils/helpers';
import { NameByValue } from '@/types';

export const ON_CURRENT_CASE_SECTION = 'On current case';
export const ON_REFERENCE_CASE_SECTION = 'On reference case';

export const getRestrictedStartLocationIds = (
  routeType: RouteTypeSettingsEnum,
  donorHospitalId?: string,
  txCenterId?: string,
): Set<string> => {
  const restrictedIds = [
    routeType === RouteTypeSettingsEnum.Custom_DH_TXC_Hub ? donorHospitalId : null,
    txCenterId,
  ].filter((id): id is string => !!id);

  return new Set(restrictedIds);
};

export type StartLocationListItemType = NameByValue & {
  section?: string;
  data: StartLocationType | null;
};

export const getLocationListItem = (
  item: RouteLocationType,
  section: string = ON_CURRENT_CASE_SECTION,
): StartLocationListItemType => {
  switch (item.locationType) {
    case LegLocationTypeEnum.hub:
      return {
        value: item.id,
        name: `Hub: NOP ${item.code}`,
        section,
        data: {
          type: StartLocationObjectType.Hub,
          id: item.id,
          code: item.code,
          location: item.location,
        },
      };
    case LegLocationTypeEnum.hospital:
      return {
        value: item.id,
        name: `DH: ${item.location.city}, ${item.location.state || item.location.country}`,
        section,
        data: {
          type: StartLocationObjectType.DH,
          id: item.id,
          code: item.code,
          location: item.location,
        },
      };
    case LegLocationTypeEnum.txcenter:
      return {
        value: item.id,
        name: `TXC: ${item.location.city}, ${item.location.state || item.location.country}`,
        section,
        data: {
          type: StartLocationObjectType.TXC,
          id: item.id,
          code: item.code,
          location: item.location,
        },
      };
    case LegLocationTypeEnum.airport:
      return {
        value: item.id,
        name: `FBO: ${item.iata || item.icao}`,
        section,
        data: {
          type: StartLocationObjectType.Airport,
          ...item,
        },
      };
    case LegLocationTypeEnum.homeResidence: {
      const homeTitle = `Home: ${getLocationAddress(item.location)}`;

      return {
        value: item.id,
        name: homeTitle,
        section,
        data: {
          type: StartLocationObjectType.Custom,
          id: item.id,
          location: item.location,
          customTitle: homeTitle,
        },
      };
    }
    default: {
      const customTitle = `Custom: ${getLocationAddress(item.location)}`;

      return {
        value: item.id,
        name: customTitle,
        section,
        data: {
          type: StartLocationObjectType.Custom,
          id: item.id,
          location: item.location,
          customTitle,
        },
      };
    }
  }
};

export const getLocationCountryCodes = (items: StartLocationListItemType[]): string[] =>
  uniq(items.map((l) => l.data?.location.country)).filter((countryCode): countryCode is string => Boolean(countryCode));

export const getStartLocationString = (data: StartLocationType | null): string => {
  if (!data) return 'Select';

  switch (data.type) {
    case StartLocationObjectType.Hub:
      return `Hub: NOP ${data.code}`;
    case StartLocationObjectType.DH:
      return `DH: ${data.location.city}, ${data.location.state || data.location.country}`;
    case StartLocationObjectType.TXC:
      return `TXC: ${data.location.city}, ${data.location.state || data.location.country}`;
    case StartLocationObjectType.Airport:
      return `FBO: ${data.iata || data.icao}`;
    default:
      return data.customTitle || `Custom: ${getLocationAddress(data.location)}`;
  }
};
